import { ArrowDownLeft, ArrowUpRight, Filter, Layers, RotateCcw, ZoomIn, ZoomOut, ChevronDown } from "lucide-react";
import { Reveal } from "./motion";
import { SankeyFlow } from "./SankeyFlow";

const LEGEND = [
  { label: "Income", color: "#3BA776" },
  { label: "Housing", color: "hsl(var(--primary))" },
  { label: "Transport", color: "#6E8BB8" },
  { label: "Lifestyle", color: "#C9A96A" },
  { label: "Savings", color: "#8C82B6" },
];

const GUIDE = [
  { icon: ArrowDownLeft, color: "#3BA776", title: "Money in", body: "Salary, freelance, anything you earn — on the left." },
  { icon: Layers, color: "hsl(var(--primary))", title: "Through your accounts", body: "Everything pools into what you actually have to spend." },
  { icon: ArrowUpRight, color: "#6E8BB8", title: "Where it goes", body: "Grouped by AI into housing, living, savings and the rest." },
];

export function MoneyFlowSection() {
  return (
    <section id="money-flow" className="bg-card">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <Reveal>
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-sm font-bold uppercase tracking-widest text-primary-text">The money-flow diagram</span>
            <h2 className="mt-4 text-4xl font-extrabold tracking-tight text-foreground sm:text-[2.6rem]">
              One picture. Your whole month.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              This is the view I built Budge around. Money comes in on the left, runs through your
              accounts, and fans out to everything you spend on — sized so the big drains are obvious.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1} y={32}>
          <div className="mt-12 rounded-3xl border border-border bg-background p-5 shadow-xl sm:p-7">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <div className="text-xl font-bold text-foreground">Money Flow</div>
                <div className="text-sm font-medium text-muted-foreground">September 2026 · All accounts</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground">
                  This month <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                </span>
                {[ZoomOut, ZoomIn, RotateCcw].map((Icon, i) => (
                  <span key={i} className="grid h-9 w-9 place-items-center rounded-xl border border-border bg-card text-muted-foreground">
                    <Icon className="h-4 w-4" />
                  </span>
                ))}
                <span className="flex items-center gap-1.5 rounded-full bg-primary/10 px-3.5 py-2 text-sm font-semibold text-primary-text">
                  <Filter className="h-3.5 w-3.5" /> Filters
                </span>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between px-1 font-mono text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">
              <span>Sources</span>
              <span>Net flow</span>
              <span>Where it goes</span>
            </div>

            <div className="mt-2">
              <SankeyFlow />
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 px-1">
              {LEGEND.map((l) => (
                <span key={l.label} className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <span className="h-2 w-2 rounded-full" style={{ background: l.color }} />
                  {l.label}
                </span>
              ))}
              <span className="ml-auto text-xs font-medium text-muted-foreground/70">
                Drag any block to explore · AI groups your categories
              </span>
            </div>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-8 sm:grid-cols-3">
          {GUIDE.map((g, i) => (
            <Reveal key={g.title} delay={0.1 + i * 0.08}>
              <div>
                <span className="grid h-10 w-10 place-items-center rounded-xl bg-muted/60">
                  <g.icon className="h-5 w-5" style={{ color: g.color }} />
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground">{g.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{g.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
